import { WeatherData } from '../types/weather.types';
import { LocationData } from '../types/location.types';
import { StorageHelper, CACHE_TTL } from '../utils/storageHelpers';

export class WeatherCache {
  private static readonly WEATHER_KEY_PREFIX = 'weather_';
  private static readonly LOCATION_KEY = 'location_data';

  // In-memory cache for current session
  private static memoryCache: Map<string, { data: WeatherData; timestamp: number }> = new Map();

  private static getWeatherKey(latitude: number, longitude: number): string {
    // Round to 2 decimal places to group nearby locations
    const lat = latitude.toFixed(2);
    const lon = longitude.toFixed(2);
    return `${this.WEATHER_KEY_PREFIX}${lat}_${lon}`;
  }

  static setWeather(
    latitude: number,
    longitude: number,
    data: WeatherData,
    source: string = 'api'
  ): void {
    const key = this.getWeatherKey(latitude, longitude);

    // Save to memory cache
    this.memoryCache.set(key, {
      data,
      timestamp: Date.now(),
    });

    // Save to localStorage
    StorageHelper.set(key, data, CACHE_TTL.weather, source);
  }

  static getWeather(latitude: number, longitude: number): {
    data: WeatherData;
    cached: boolean;
    age: number;
    source: string;
  } | null {
    const key = this.getWeatherKey(latitude, longitude);

    // Try memory cache first (faster)
    const memoryEntry = this.memoryCache.get(key);
    if (memoryEntry) {
      const age = Date.now() - memoryEntry.timestamp;
      if (age < CACHE_TTL.weather) {
        return {
          data: memoryEntry.data,
          cached: true,
          age,
          source: 'memory',
        };
      } else {
        // Expired, remove from memory
        this.memoryCache.delete(key);
      }
    }

    // Try localStorage
    const cacheInfo = StorageHelper.getCacheInfo<WeatherData>(key);
    if (cacheInfo) {
      // Also update memory cache
      this.memoryCache.set(key, {
        data: cacheInfo.data,
        timestamp: Date.now() - cacheInfo.age,
      });

      return {
        data: cacheInfo.data,
        cached: true,
        age: cacheInfo.age,
        source: cacheInfo.source,
      };
    }

    return null;
  }

  static setLocation(data: LocationData, source: string = 'ip'): void {
    StorageHelper.set(this.LOCATION_KEY, data, CACHE_TTL.location, source);
  }

  static getLocation(): {
    data: LocationData;
    age: number;
    source: string;
  } | null {
    const cacheInfo = StorageHelper.getCacheInfo<LocationData>(this.LOCATION_KEY);
    return cacheInfo;
  }

  static clearWeatherCache(): void {
    // Clear memory cache
    this.memoryCache.clear();

    // Clear localStorage weather entries
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(this.WEATHER_KEY_PREFIX)) {
        StorageHelper.remove(key);
      }
    });
  }

  static clearLocationCache(): void {
    StorageHelper.remove(this.LOCATION_KEY);
  }

  static clearAll(): void {
    this.clearWeatherCache();
    this.clearLocationCache();
  }

  static getCacheStats(): {
    weatherEntries: number;
    totalSize: number;
    oldestEntry: number;
  } {
    const keys = Object.keys(localStorage);
    const weatherKeys = keys.filter(key => key.startsWith(this.WEATHER_KEY_PREFIX));

    let oldestEntry = Date.now();
    weatherKeys.forEach(key => {
      const cacheInfo = StorageHelper.getCacheInfo(key);
      if (cacheInfo && cacheInfo.age > 0) {
        const entryTime = Date.now() - cacheInfo.age;
        if (entryTime < oldestEntry) {
          oldestEntry = entryTime;
        }
      }
    });

    return {
      weatherEntries: weatherKeys.length,
      totalSize: StorageHelper.getSize(),
      oldestEntry: Date.now() - oldestEntry,
    };
  }
}
